import React, { useState, useEffect, useCallback } from 'react';
import './HotelConfirmation.css';
import { apiRequest, parseResponse, extractHotelsList } from './utils/api';
import { useLanguage } from './i18n/LanguageContext';

const HotelConfirmation = ({ latitude, longitude, onConfirmLocation, onEditHotel }) => {
  const { t } = useLanguage();
  const [hotel, setHotel] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [failureKind, setFailureKind] = useState(null);
  const [retryKey, setRetryKey] = useState(0);

  const fetchNearbyHotels = useCallback(async () => {
    setLoadError(null);
    setFailureKind(null);
    setHotel(null);
    setLoading(true);
    try {
      const response = await apiRequest(
        `/hotels/nearby?latitude=${latitude}&longitude=${longitude}`,
        {
          method: 'GET'
        }
      );

      if (!response.ok) {
        setFailureKind('network');
        setLoadError(
          `We couldn't reach the hotel list (${response.status}). Please try again.`
        );
        return;
      }

      const parsed = await parseResponse(response);
      const hotels = extractHotelsList(parsed);

      if (hotels === null) {
        setFailureKind('invalid');
        setLoadError(
          'The hotel list could not be read. Please try again in a moment.'
        );
        return;
      }

      if (hotels.length > 0) {
        setHotel(hotels[0]);
      } else {
        setFailureKind('empty');
        setLoadError(
          'No hotels were found near this location. You can try again or choose your hotel on the map.'
        );
      }
    } catch (err) {
      console.error(err);
      setFailureKind('network');
      setLoadError(
        'We could not load nearby hotels. Check your connection and tap Retry.'
      );
    } finally {
      setLoading(false);
    }
  }, [latitude, longitude]);

  useEffect(() => {
    if (latitude === null || longitude === null) {
      return;
    }
    fetchNearbyHotels();
  }, [latitude, longitude, retryKey, fetchNearbyHotels]);

  const handleRetry = () => {
    setRetryKey((k) => k + 1);
  };

  if (loading) {
    return (
      <div className="hotel-confirmation-screen">
        <div className="loading-state">
          <p>{t('Finding nearby hotels...')}</p>
        </div>
      </div>
    );
  }

  if (loadError || !hotel) {
    const title =
      failureKind === 'empty'
        ? 'No hotels found'
        : failureKind === 'invalid'
          ? 'Something went wrong'
          : "Couldn't load hotels";

    return (
      <div className="hotel-confirmation-screen">
        <div className="hotel-empty-state">
          <h2 className="hotel-empty-title">{title}</h2>
          <p className="hotel-empty-message">{t(loadError || 'No hotel data is available right now.')}</p>
          <div className="hotel-empty-actions">
            <button type="button" className="confirm-btn hotel-retry-btn" onClick={handleRetry}>
              <span className="confirm-btn-text">{t('Retry')}</span>
            </button>
            {typeof onEditHotel === 'function' && (
              <button type="button" className="hotel-secondary-btn" onClick={onEditHotel}>
                {t('Pick on map')}
              </button>
            )}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="hotel-confirmation-screen">
      <div className="confirmation-question-section">
        <h1 className="confirmation-question">
          <span className="question-lead">{t('Staying at')}</span>{' '}
          <span className="question-name">{hotel.hotelName}?</span>
        </h1>
      </div>

      <div className="hotel-card-container">  
        <div className="hotel-card">
          <div className="hotel-details">
            <div className="hotel-name-row">
              <div className="hotel-name-location">
                <h2 className="hotel-name">{hotel.hotelName}</h2>
                <p className="hotel-location">{hotel.location || hotel.city || 'New Delhi'}</p>
              </div>
              <button className="edit-link" onClick={onEditHotel}>
                <span className="edit-link-text">{t('Edit')}</span>
              </button>
            </div>

            <div className="pickup-point-section">
              <p className="pickup-label">{t('Pickup point')}</p>
              <div className="pickup-point">
                <div className="pickup-dot"></div>
                <span className="pickup-text">{t('Hotel Porch')}</span>
              </div>
            </div>
          </div>

          <div className="hotel-image-wrapper">
            <img 
              src={hotel.imageUrl || 'https://placehold.co/364x193'} 
              alt={hotel.hotelName}
              className="hotel-image"
              onError={(e) => {
                e.target.src = 'https://imgs.search.brave.com/KoMKnms7LcVDZ1UQLMPunHZqT7CI08fQBclKFmLzG68/rs:fit:860:0:0:0/g:ce/aHR0cHM6Ly9tZWRp/YS5nZXR0eWltYWdl/cy5jb20vaWQvMTU3/NjgxMTk4L3Bob3Rv/L2RhbmRlbGlvbi1z/ZWVkLmpwZz9zPTYx/Mng2MTImdz0wJms9/MjAmYz1UZmhDdGNf/WTFvU3BkbEw3bzZ5/bzdCVGZnbkQwUkN5/NnlGMVNndlFPMEpr/PQ';
              }}
            />
            <div className="hotel-image-gradient"></div>
          </div>
        </div>
      </div>

      <button 
        className="confirm-btn" 
        onClick={() => onConfirmLocation(hotel)}
      >
        <span className="confirm-btn-text">{t('Confirm Location')}</span>
      </button>
    </div>
  );
};

export default HotelConfirmation;
